import * as Chart from 'chart.js';
// Register before charts are created; lazy registration leaves existing charts uninitialized.
import 'chartjs-plugin-zoom';
import { BehaviorSubject, filter, map, take, takeUntil } from 'rxjs';

import { OnInit, Component, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { CoreComponent } from './core/core.component';
import { CookieConsentFormDialogComponent } from './cookie-consent-form/cookie-consent-form-dialog.component';

import { CookieService } from 'ngx-cookie-service';
import { AuthService } from './modules/admin/auth/services/auth.service';

import { MODAL_WIDTH } from './utility/app.util';

import { HUB_SPOT_COOKIE_NAMES } from '@app/util';
import { HubSpotChatService } from './core/hubspot/hubspot-chat.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends CoreComponent implements OnInit, AfterViewChecked {
  @ViewChild('alertBanner', { read: ElementRef }) alertBannerRef!: ElementRef;

  public isAdminRoute: boolean = false;
  public isSociuuHubRoute: boolean = false;
  public isHubSpotEnabled: boolean = false;

  // isBannerHidden default value should be true in order to make sure that the banner is hidden by default
  public isBannerHidden$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isBannerEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Banner configs
  // When turning on/off the banner always remember to change the banner type
  private bannerCookie: string = '';
  private currentBannerType: string = 'myhubAnnouncement';

  private allowBannerForAdmins: boolean = false;
  private allowBannerForSociuuhub: boolean = false;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private cookie: CookieService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private hubSpotChatService: HubSpotChatService,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setupGlobalErrorHandlers();
    this.setBannerCookie();
    this.observeRouteChanges();
  }

  public ngAfterViewChecked(): void {
    this.registerChartPlugins();
    this.calculateBannerHeight();
  }

  public closeBanner(): void {
    this.cookie.set('currentBannerType', this.currentBannerType);
    this.setBannerCookie();
  }

  private setBannerCookie(): void {
    this.bannerCookie = this.cookie.get('currentBannerType');
    if (!this.isBannerHidden$.value) {
      this.isBannerHidden$.next(this.currentBannerType === this.bannerCookie);
      this.setBannerVisibility();
    }
  }

  private observeRouteChanges(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        filter(event => event instanceof NavigationEnd),
        map((): any => {
          let child = this.route.firstChild;
          const data: Record<string, unknown> = {};

          while (child) {
            Object.assign(data, child.snapshot.data);
            child = child.firstChild;
          }

          return Object.keys(data).length ? data : null;
        })
      )
      .subscribe((response: any): void => {
        if (response) {
          this.isAdminRoute = response.isAdmin;
          this.isSociuuHubRoute = response.isSociuuhub;
          this.isHubSpotEnabled = response.isHubspotEnabled;

          this.actionsAfterRouteChange();
        } else {
          this.isAdminRoute = false;
          this.isSociuuHubRoute = false;
          this.isHubSpotEnabled = false;
          this.actionsAfterRouteChange();
        }
      });
  }

  private actionsAfterRouteChange(): void {
    this.setBannerVisibility();

    if (this.isSociuuHubRoute || !this.isHubSpotEnabled) {
      this.hubSpotChatService.remove();
      if (!this.isHubSpotEnabled) {
        this.clearHubSpotCookies();
      }

      return;
    }

    this.checkCookieConsentOnLoad();
  }

  private setBannerVisibility(): void {
    const isBannerEnabledForAdmin: boolean = this.isAdminRoute && this.allowBannerForAdmins;
    const isBannerEnabledForSociuuhub: boolean = this.isSociuuHubRoute && this.allowBannerForSociuuhub;
    this.isBannerEnabled$.next(!this.isBannerHidden$.value && (isBannerEnabledForAdmin || isBannerEnabledForSociuuhub));
  }

  private calculateBannerHeight(): void {
    if (this.isBannerEnabled$.value && this.alertBannerRef) {
      const bannerHeight: boolean = this.alertBannerRef.nativeElement.offsetHeight;

      // Set CSS custom property instead of attribute
      document.documentElement.style.setProperty('--banner-height', `${bannerHeight}px`);
    } else {
      document.documentElement.style.setProperty('--banner-height', '0px');
    }
  }

  private registerChartPlugins(): void {
    Chart.plugins.register({
      afterDraw: (chart: any) => {
        if (chart.data.datasets[0].data.length === 0) {
          // No data is present
          const ctx = chart.chart.ctx;
          const width = chart.chart.width;
          const height = chart.chart.height;
          chart.clear();

          ctx.save();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = '16px bold';
          ctx.fillText('Sorry there wasn\'t enough data', width / 2, height / 2);
          ctx.fillText('to show statistics', width / 2, height / 2 + 16);
          ctx.restore();
        }
      },
    });
  }

  private checkCookieConsentOnLoad(): void {
    this.authService.clientInfo.pipe(filter(Boolean), take(1)).subscribe(() => {
      if (this.authService.shouldShowCookieConsent) {
        this.showCookieConsentDialog();
      } else if (this.shouldInitializeHubSpot()) {
        void this.hubSpotChatService.initialize();
      }
    });
  }

  private showCookieConsentDialog(): void {
    const dialogRef = this.dialog.open(CookieConsentFormDialogComponent, {
      minWidth: MODAL_WIDTH.MD,
      hasBackdrop: true,
      disableClose: true,
      closeOnNavigation: false,
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (response === true && this.shouldInitializeHubSpot()) {
        void this.hubSpotChatService.initialize();
      }
    });
  }

  private shouldInitializeHubSpot(): boolean {
    return this.isHubSpotEnabled && !this.isSociuuHubRoute;
  }

  private setupGlobalErrorHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);

      // Check if it's an XMLHttpRequestProgressEvent
      if (event.reason && event.reason.constructor && event.reason.constructor.name === 'XMLHttpRequestProgressEvent') {
        console.warn('XMLHttpRequestProgressEvent caught and handled');
        event.preventDefault(); // Prevent the error from being logged to console
        return;
      }

      // Handle other types of promise rejections
      if (event.reason && typeof event.reason === 'object') {
        if (event.reason.message && event.reason.message.includes('XMLHttpRequest')) {
          console.warn('XMLHttpRequest error caught and handled');
          event.preventDefault();
          return;
        }
      }

      // Log other unhandled rejections but don't prevent them
      console.error('Unhandled promise rejection details:', {
        reason: event.reason,
        type: typeof event.reason,
        constructor: event.reason?.constructor?.name
      });
    });

    // Handle general errors
    window.addEventListener('error', (event) => {
      console.error('Global error caught:', event.error);

      // Check for XMLHttpRequest related errors
      if (event.error && event.error.message && event.error.message.includes('XMLHttpRequest')) {
        console.warn('XMLHttpRequest error in global handler');
        return;
      }
    });
  }

  private clearHubSpotCookies(): void {
    // Only remove HubSpot cookies that are set on THIS domain. Do not touch other domains.
    const hubSpotCookieNames = HUB_SPOT_COOKIE_NAMES;
    const host = window.location.hostname;

    for (const name of hubSpotCookieNames) {
      // Delete only on current domain variants
      this.cookie.delete(name);
      this.cookie.delete(name, '/');
      this.cookie.delete(name, '/', host);
      this.cookie.delete(name, '/', '.' + host);
    }
  }
}
